export const PRG_CODE_TEMPLATE = "{CAMPAIGN_ID}/{BANNER_ID}/{PRG}/{SITE_ID}";
export const DEFAULT_UTM_MARKERS = `utm_source=Programmatic&prg_code=${PRG_CODE_TEMPLATE}`;
export const RETARGEO_UTM_MARKERS = `utm_source=Retargeo&prg_code=${PRG_CODE_TEMPLATE}`;
export const V1_UTM_MARKERS =
  "utm_source=Programmatic&utm_medium={PRG}&utm_campaign={CAMPAIGN_ID}&utm_content={BANNER_ID}&utm_term={SITE_ID}";
export const V2_UTM_MARKERS =
  "utm_source=Programmatic&utm_medium={PRG}&utm_term={CAMPAIGN_ID}/{BANNER_ID}/{SITE_ID}";

export const OUR_UTM_MACROS = ["PRG", "CAMPAIGN_ID", "BANNER_ID", "SITE_ID"];
export const OUR_MACRO_ITEMS = [
  { token: "{CAMPAIGN_ID}", name: "CAMPAIGN_ID", label: "Кампания" },
  { token: "{BANNER_ID}", name: "BANNER_ID", label: "Баннер" },
  { token: "{PRG}", name: "PRG", label: "Партнёр" },
  { token: "{SITE_ID}", name: "SITE_ID", label: "Площадка" },
];
export const UTM_MACRO_FIELDS = ["utm_medium", "utm_campaign", "utm_content", "utm_term"];
export const NEW_UTM_TERM_REGEX =
  /^\{CAMPAIGN_ID\}\/\{BANNER_ID\}(?:\/\{PRG\}(?:\/\{SITE_ID\})?|\/\{SITE_ID\})?$/;
export const PRG_CODE_MACRO_REGEX =
  /^\{CAMPAIGN_ID\}\/\{BANNER_ID\}\/\{PRG\}\/\{SITE_ID\}$/;
export const MARKUP_VERSION_LABELS = {
  v1: "версия 1 — старая UTM",
  v2: "версия 2 — utm_term",
  v3: "версия 3 — prg_code",
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_SRC =
  "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
const FILLED_NEW_TERM_REGEX = new RegExp(
  `^${UUID_SRC}/${UUID_SRC}(?:/PRG\\d+(?:/[^/&]+)?|/[^/&]+)?$`,
  "i"
);
const FILLED_PRG_CODE_REGEX = new RegExp(
  `^${UUID_SRC}/${UUID_SRC}/PRG\\d+(?:/[^/&]+)?$`,
  "i"
);
const THIRD_PARTY_MACRO_RE =
  /\{[^{}]+\}|\$\{[^}]+\}|%%[^%]+%%|%[A-Za-z][\w.]*%|\[[A-Za-z0-9_]+\]/g;
const OUR_MACRO_TOKEN_RE = /\{(PRG|CAMPAIGN_ID|BANNER_ID|SITE_ID)\}/g;
const THIRD_PARTY_KEYS = new Set(["other", "code"]);
const SOURCE_VALUES = new Set(["programmatic", "retargeo"]);

const decodeURIComponentSafe = (value) => {
  try {
    return decodeURIComponent(String(value || "").replace(/\+/g, " "));
  } catch {
    return String(value || "");
  }
};

const findMacroTokens = (value) => {
  const text = decodeURIComponentSafe(value);
  const tokens = text.match(THIRD_PARTY_MACRO_RE) || [];
  return [...new Set(tokens)];
};

const isOurMacroToken = (token) => {
  const inner = String(token || "").replace(/^\{|\}$/g, "");
  return OUR_UTM_MACROS.includes(inner);
};

const foreignMacrosIn = (value) => findMacroTokens(value).filter((token) => !isOurMacroToken(token));

export const parseUtmMarkerParams = (value) => {
  const params = {};
  String(value || "")
    .split("&")
    .forEach((part) => {
      if (!part) return;
      const eq = part.indexOf("=");
      if (eq === -1) {
        params[part] = "";
        return;
      }
      params[part.slice(0, eq)] = part.slice(eq + 1);
    });
  return params;
};

export const utmPlaceholderNames = (value) => {
  const found = String(value || "").match(/\{([^{}]+)\}/g) || [];
  return found.map((placeholder) => placeholder.replace(/[{}]/g, ""));
};

const paramsWithoutThirdParty = (params) =>
  Object.fromEntries(Object.entries(params).filter(([key]) => !THIRD_PARTY_KEYS.has(key)));

const UTM_LAYOUT_FIELDS = ["utm_medium", "utm_campaign", "utm_content", "utm_term"];
const GLUED_MACROS_RE = /\}[ \t]*\{/;

const macrosInField = (value) => String(value || "").match(OUR_MACRO_TOKEN_RE) || [];

const fieldHasMacro = (value, token) => String(value || "").includes(token);

const fieldMacroProblems = (field, value) => {
  const problems = [];
  const text = String(value || "");
  if (!text) return problems;
  if (GLUED_MACROS_RE.test(text)) {
    problems.push(`Макросы в ${field} склеены. Между ними нужен слэш или текст, нельзя писать {A}{B}`);
  }
  const seen = {};
  macrosInField(text).forEach((token) => {
    seen[token] = (seen[token] || 0) + 1;
  });
  Object.entries(seen).forEach(([token, count]) => {
    if (count > 1) {
      problems.push(`Макрос ${token} в ${field} повторён ${count} раза. Каждый макрос в поле — один раз`);
    }
  });
  return problems;
};

const collectLayoutProblems = (params) => {
  const problems = [];
  const campaign = params.utm_campaign || "";
  const content = params.utm_content || "";
  const term = params.utm_term || "";
  const medium = params.utm_medium || "";
  const prgCode = params.prg_code || "";
  const format = formatAlias(detectFormatWithMacros(params));

  UTM_LAYOUT_FIELDS.forEach((field) => {
    problems.push(...fieldMacroProblems(field, params[field] || ""));
  });

  if (format === "v3" || prgCode) {
    if (!PRG_CODE_MACRO_REGEX.test(prgCode)) {
      problems.push("Версия 3: prg_code должен быть {CAMPAIGN_ID}/{BANNER_ID}/{PRG}/{SITE_ID}");
    }
    return { format: format || "v3", problems };
  }

  if (format === "v2") {
    if (!NEW_UTM_TERM_REGEX.test(term)) {
      problems.push("Версия 2: utm_term должен быть {CAMPAIGN_ID}/{BANNER_ID}, дальше можно /{PRG} и /{SITE_ID} в этом порядке");
    }
    return { format, problems };
  }

  if (!fieldHasMacro(campaign, "{CAMPAIGN_ID}") || !fieldHasMacro(content, "{BANNER_ID}")) {
    if (!`${medium}&${campaign}&${content}&${term}`.includes("{CAMPAIGN_ID}") || !`${medium}&${campaign}&${content}&${term}`.includes("{BANNER_ID}")) {
      problems.push("Для версии 1 в метках должны быть {CAMPAIGN_ID} в utm_campaign и {BANNER_ID} в utm_content");
    } else {
      if (!fieldHasMacro(campaign, "{CAMPAIGN_ID}")) {
        problems.push("Версия 1: {CAMPAIGN_ID} должен стоять в utm_campaign");
      }
      if (!fieldHasMacro(content, "{BANNER_ID}")) {
        problems.push("Версия 1: {BANNER_ID} должен стоять в utm_content");
      }
    }
  }
  if (fieldHasMacro(content, "{CAMPAIGN_ID}") || fieldHasMacro(term, "{CAMPAIGN_ID}")) {
    problems.push("Нельзя переставлять макросы: {CAMPAIGN_ID} должен быть в utm_campaign, не в content и не в term");
  }
  if (fieldHasMacro(campaign, "{BANNER_ID}") || fieldHasMacro(term, "{BANNER_ID}")) {
    problems.push("Нельзя переставлять макросы: {BANNER_ID} должен быть в utm_content, не в campaign и не в term");
  }
  if (fieldHasMacro(campaign, "{PRG}") || fieldHasMacro(content, "{PRG}")) {
    problems.push("Нельзя переставлять макросы: {PRG} в версии 1 стоит в utm_medium, не в campaign и не в content");
  }
  return { format: format || "v1", problems };
};

export const isAllowedUtmMarkersLayout = (value, programmaticSourcePattern = "Programmatic") => {
  if (String(value || "").length > 512) return false;
  const params = parseUtmMarkerParams(value);
  const source = params.utm_source || "";
  const sourcePattern = new RegExp(`^(?:${programmaticSourcePattern}|Retargeo)$`, "i");
  if (!sourceOk(source) && !sourcePattern.test(source)) return false;

  const layoutParams = paramsWithoutThirdParty(params);

  const foreignOutsideOther = Object.entries(params)
    .filter(([key]) => !THIRD_PARTY_KEYS.has(key))
    .flatMap(([, val]) => foreignMacrosIn(val));
  if (foreignOutsideOther.length) return false;

  const foundPlaceholders = utmPlaceholderNames(
    Object.entries(layoutParams)
      .map(([, val]) => val)
      .join("&")
  );
  if (!foundPlaceholders.every((name) => OUR_UTM_MACROS.includes(name))) return false;

  return collectLayoutProblems(layoutParams).problems.length === 0;
};

const queryFromInput = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) {
    try {
      const url = new URL(s);
      return url.search.startsWith("?") ? url.search.slice(1) : url.search;
    } catch {
      const q = s.indexOf("?");
      return q === -1 ? "" : s.slice(q + 1);
    }
  }
  return s.startsWith("?") ? s.slice(1) : s;
};

const utmMarkersFromQuery = (query) =>
  String(query || "")
    .split("&")
    .filter((part) => {
      const key = part.split("=")[0].toLowerCase();
      return key.startsWith("utm_") || key === "prg_code" || THIRD_PARTY_KEYS.has(key);
    })
    .join("&");

const ourMacrosIn = (value) => {
  const found = String(value || "").match(OUR_MACRO_TOKEN_RE) || [];
  return [...new Set(found)];
};

const sourceOk = (source) => SOURCE_VALUES.has(String(source || "").trim().toLowerCase());

export const detectMarkupVersion = (value) => {
  const params = parseUtmMarkerParams(queryFromInput(value) || value);
  return detectFormatWithMacros(params) || detectFormatFilled(params);
};

export const markupVersionFromValue = (value) => {
  const detected = detectMarkupVersion(value);
  if (detected) return detected;
  const params = parseUtmMarkerParams(queryFromInput(value) || value);
  if (params.prg_code) return "v3";
  return "v2";
};

const detectFormatWithMacros = (params) => {
  const prgCode = params.prg_code || "";
  if (PRG_CODE_MACRO_REGEX.test(prgCode) || prgCode.includes("{CAMPAIGN_ID}")) return "v3";
  const term = params.utm_term || "";
  if (NEW_UTM_TERM_REGEX.test(term) || term.includes("{CAMPAIGN_ID}")) return "v2";
  if ((params.utm_campaign || "").includes("{CAMPAIGN_ID}")) return "v1";
  return null;
};

const detectFormatFilled = (params) => {
  const prgCode = params.prg_code || "";
  if (FILLED_PRG_CODE_REGEX.test(prgCode)) return "v3";
  const term = params.utm_term || "";
  if (FILLED_NEW_TERM_REGEX.test(term)) return "v2";
  if (UUID_RE.test(params.utm_campaign || "") && UUID_RE.test(params.utm_content || "")) return "v1";
  return null;
};

const formatAlias = (format) => {
  if (format === "old") return "v1";
  if (format === "new") return "v2";
  return format;
};

export const inspectInternalUtm = (raw) => {
  const issues = [];
  const query = queryFromInput(raw);
  if (!String(raw || "").trim()) {
    return { ok: false, format: null, issues: [{ tone: "muted", text: "Вставьте ссылку или строку UTM-меток" }] };
  }
  if (!query) {
    return { ok: false, format: null, issues: [{ tone: "bad", text: "В ссылке нет query-параметров" }] };
  }

  const params = parseUtmMarkerParams(query);
  const markers = utmMarkersFromQuery(query);
  const layoutParams = paramsWithoutThirdParty(params);
  const { format, problems: layoutProblems } = collectLayoutProblems(layoutParams);
  const layoutOk = isAllowedUtmMarkersLayout(markers, "Programmatic|programmatic");

  if (!params.utm_source) {
    issues.push({ tone: "bad", text: "Нет utm_source. Должен быть Programmatic или Retargeo" });
  } else if (!sourceOk(params.utm_source)) {
    issues.push({
      tone: "bad",
      text: `utm_source=${params.utm_source} не подойдёт. Для кабинета используйте Programmatic или Retargeo`,
    });
  }

  if (markers.length > 512) {
    issues.push({ tone: "bad", text: "Строка разметки длиннее 512 символов" });
  }

  const leftoverForeign = Object.entries(params)
    .filter(([key]) => !THIRD_PARTY_KEYS.has(key.toLowerCase()))
    .flatMap(([, val]) => foreignMacrosIn(val));
  if (leftoverForeign.length) {
    issues.push({
      tone: "bad",
      text: `Сторонние макросы (${leftoverForeign.join(", ")}) нельзя ставить в UTM. Добавьте их в поле other`,
    });
  }

  if (params.other || params.code) {
    issues.push({
      tone: "ok",
      text: "Макросы сторонних систем стоят в other — так и нужно",
    });
  }

  layoutProblems.forEach((text) => {
    issues.push({ tone: "bad", text });
  });

  if (layoutProblems.length === 0) {
    if (format === "v3") {
      issues.push({
        tone: "ok",
        text: "Версия 3: статистика идёт из prg_code. Наши макросы можно дублировать в UTM, но без склейки и повторов в одном поле",
      });
    } else if (format === "v2") {
      issues.push({
        tone: "ok",
        text: "Версия 2: макросы стоят в UTM, статистика читается из utm_term",
      });
    } else {
      issues.push({
        tone: "ok",
        text: "Версия 1: кампания в utm_campaign, баннер в utm_content, PRG в utm_medium",
      });
    }
  }

  const v3Ok = format === "v3" && PRG_CODE_MACRO_REGEX.test(params.prg_code || "") && layoutProblems.length === 0;
  const ok = layoutOk && leftoverForeign.length === 0 && layoutProblems.length === 0;
  return { ok, format, issues, v3Ok };
};

export const inspectExternalUtm = (raw) => {
  const issues = [];
  const query = queryFromInput(raw);
  if (!String(raw || "").trim()) {
    return { ok: false, format: null, issues: [{ tone: "muted", text: "Вставьте итоговую ссылку без наших макросов" }] };
  }
  if (!query) {
    return { ok: false, format: null, issues: [{ tone: "bad", text: "В ссылке нет query-параметров" }] };
  }

  const params = parseUtmMarkerParams(query);
  const leftovers = ourMacrosIn(query);
  const format = formatAlias(detectFormatFilled(params));

  if (leftovers.length) {
    issues.push({
      tone: "bad",
      text: `Для сторонней площадки нельзя оставлять наши макросы: ${leftovers.join(", ")}. Подставьте значения или уберите их`,
    });
  }

  if (!params.utm_source) {
    issues.push({ tone: "bad", text: "Нет utm_source. Должен быть Programmatic или Retargeo" });
  } else if (!sourceOk(params.utm_source)) {
    issues.push({
      tone: "bad",
      text: `utm_source=${params.utm_source} не подойдёт для статистики. Нужен Programmatic или Retargeo`,
    });
  }

  const foreignOutsideOther = Object.entries(params)
    .filter(([key]) => !THIRD_PARTY_KEYS.has(key.toLowerCase()))
    .flatMap(([, val]) => foreignMacrosIn(val));
  if (foreignOutsideOther.length) {
    issues.push({
      tone: "bad",
      text: `Сторонние макросы (${foreignOutsideOther.join(", ")}) должны быть только в &other=, не в UTM и не в prg_code`,
    });
  } else if (params.other || params.code) {
    issues.push({ tone: "ok", text: "Сторонние макросы стоят в other — так и нужно" });
  } else {
    issues.push({
      tone: "warn",
      text: "Параметра other нет. Если площадка требует свой макрос, добавьте его в поле other",
    });
  }

  if (!leftovers.length) {
    if (format === "v3") {
      issues.push({ tone: "ok", text: "Версия 3 заполнена: campaign, banner, PRG и площадка стоят в prg_code" });
    } else if (format === "v2") {
      issues.push({ tone: "ok", text: "Версия 2 заполнена: campaign и banner UUID стоят в utm_term" });
    } else if (format === "v1") {
      issues.push({ tone: "ok", text: "Версия 1 заполнена: campaign UUID в utm_campaign, banner UUID в utm_content" });
    } else {
      issues.push({
        tone: "bad",
        text: "Не видно UUID кампании и баннера. В версии 3 они идут в prg_code, в версии 2 — в utm_term, в версии 1 — utm_campaign и utm_content",
      });
    }
  }

  const ok =
    leftovers.length === 0 &&
    sourceOk(params.utm_source) &&
    foreignOutsideOther.length === 0 &&
    Boolean(format);
  return { ok, format, issues };
};

export const emptyMarkupFields = (source = "Programmatic") => ({
  utm_source: source,
  utm_medium: "",
  utm_campaign: "",
  utm_content: "",
  utm_term: "",
  other: "",
});

export const templatesForVersion = (version, source = "Programmatic") => {
  const src = source || "Programmatic";
  if (version === "v1") {
    return {
      utm_source: src,
      utm_medium: "{PRG}",
      utm_campaign: "{CAMPAIGN_ID}",
      utm_content: "{BANNER_ID}",
      utm_term: "{SITE_ID}",
      other: "",
    };
  }
  if (version === "v2") {
    return {
      utm_source: src,
      utm_medium: "{PRG}",
      utm_campaign: "",
      utm_content: "",
      utm_term: "{CAMPAIGN_ID}/{BANNER_ID}/{SITE_ID}",
      other: "",
    };
  }
  return emptyMarkupFields(src);
};

export const parseMarkupFields = (markers, source = "Programmatic") => {
  const params = parseUtmMarkerParams(markers);
  return {
    utm_source: params.utm_source || source,
    utm_medium: params.utm_medium || "",
    utm_campaign: params.utm_campaign || "",
    utm_content: params.utm_content || "",
    utm_term: params.utm_term || "",
    other: params.other || params.code || "",
  };
};

export const buildMarkupString = (fields, version) => {
  const parts = [];
  const push = (key, value) => {
    if (value == null || String(value).trim() === "") return;
    parts.push(`${key}=${String(value).trim()}`);
  };
  push("utm_source", fields.utm_source);
  push("utm_medium", fields.utm_medium);
  push("utm_campaign", fields.utm_campaign);
  push("utm_content", fields.utm_content);
  push("utm_term", fields.utm_term);
  push("other", fields.other);
  if (version === "v3") {
    parts.push(`prg_code=${PRG_CODE_TEMPLATE}`);
  }
  return parts.join("&");
};

export const fillMarkupPreview = (
  markers,
  { campaignId, bannerId, prg = "PRG100", siteId = "site_id" } = {}
) =>
  String(markers || "")
    .replace(/\{CAMPAIGN_ID\}/g, campaignId || "{CAMPAIGN_ID}")
    .replace(/\{BANNER_ID\}/g, bannerId || "{BANNER_ID}")
    .replace(/\{PRG\}/g, prg)
    .replace(/\{SITE_ID\}/g, siteId);

export const composeLandingUrl = (landing, markers) => {
  const page = String(landing || "").trim();
  const query = String(markers || "").trim();
  if (!page) return query;
  if (!query) return page;
  return `${page}${page.includes("?") ? "&" : "?"}${query}`;
};
